
import * as R from 'ramda';
// constants
import * as GC from '../constants';
// helpers
import { getPropFromObject } from './getter';
import { createStringFromArray } from './string';
import { isEventTypePickup } from './events-load';
import { statesMap, countriesMap } from './options';
import { omitObjectSystemFields } from './array-object';
import { getAmousConfigByNameFromWindow } from './window';
import { getDayOfWeek, convertTimeToConfigFormat, convertInstanceToDefaultDateFormat } from './date';
// import { mapObjectEmptyStringFieldsToNull } from './array-object';
import {
  isTrue,
  ifElse,
  isNotNil,
  isAnyTrue,
  isAllTrue,
  notEquals,
  isNilOrEmpty,
  isNotNilAndNotEmpty,
} from './helpers';
//////////////////////////////////////////////////

const normalize = (str) => str.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

const getStateValueByConfig = (value) => {
  const useAbbreviation = getAmousConfigByNameFromWindow(GC.UI_USE_STATE_ABBREVIATION);
  const valueProp = useAbbreviation ? 'abbreviation' : 'labelFull';
  const options = [...GC.USA_STATES, ...GC.CANADA_PROVINCES, ...GC.MEXICO_STATES];

  const match = R.find(
    ({ labelFull, abbreviation }) => {
      const labelNormalized = normalize(labelFull);
      const valueNormalized = normalize(R.or(value, ''));

      return R.equals(value, abbreviation) || R.equals(labelNormalized, valueNormalized);
    },
    options,
  );

  if (!match) return value;

  return R.prop(valueProp, match);
};

const setStateValueByConfig = (location) => ({
  ...location,
  [GC.FIELD_STATE]: getStateValueByConfig(R.prop(GC.FIELD_STATE, location)),
});

const getShortName = (name, data) => {
  let shortName = R.pathOr(name, [name, 'shortName'], data);

  if (R.equals(shortName, 'undefined')) {
    shortName = name;
  }

  return shortName;
};

const renderFullAddressString = (location) => (
  R.compose(
    R.join(', '),
    R.filter((item) => isNotNil(item)),
    R.values(),
    R.assoc('state', getShortName(location.state, statesMap)),
    R.assoc('country', getShortName(location.country, countriesMap)),
    R.pick(['address1', 'address2', 'city', 'state', 'zip', 'country']),
  )(location)
);

const renderSmallAddressString = (location) => (
  R.compose(
    R.join(', '),
    R.filter((item) => isNotNil(item)),
    R.values(),
    R.assoc('state', getShortName(location.state, statesMap)),
    R.assoc('country', getShortName(location.country, countriesMap)),
    R.pick(['city', 'state', 'zip', 'country']),
  )(location)
);

const renderAddressNameAndCode = (location = {}) => (
  R.pathOr('', ['locationName'], location)
);

const renderAddressInfoWithNameAndCode = (
  location = {},
  withCode = true,
  withName = true,
) => {
  const { templateId, locationName, address1, address2 } = location;

  let address = R.or(address1, '');

  if (isNotNilAndNotEmpty(address2)) {
    address = createStringFromArray([address, address2], ', ');
  }

  if (R.and(withCode, templateId)) {
    address = `${templateId}, ${address}`;
  }

  if (R.and(withName, locationName)) {
    address = `${locationName}, ${address}`;
  }

  return address;
};

const concatLocationFirstAndSecondAddresses = (location = {}) => {
  if (R.isNil(R.prop(GC.FIELD_ADDRESS_2, location))) {
    return R.propOr('', GC.FIELD_ADDRESS_1, location);
  }

  return `${R.propOr('', GC.FIELD_ADDRESS_1, location)}, ${R.prop(GC.FIELD_ADDRESS_2, location)}`;
};

const concatLocationFields = (entity, fields) => {
  if (R.isNil(entity)) return '';

  const defaultFields = [GC.FIELD_CITY, GC.FIELD_STATE, GC.FIELD_ZIP, GC.FIELD_COUNTRY];

  let fieldsToPick = ifElse(
    isNilOrEmpty(fields),
    defaultFields,
    fields,
  );

  const indexOfState = R.indexOf(GC.FIELD_STATE, fieldsToPick);

  let stateValue = '';

  if (notEquals(indexOfState, -1)) {
    fieldsToPick = R.remove(indexOfState, 1, fieldsToPick);

    stateValue = getStateValueByConfig(R.prop(GC.FIELD_STATE, entity));
  }

  let locationValuesArr = R.values(
    R.pick(fieldsToPick, entity),
    entity,
  );

  if (notEquals(indexOfState, -1)) {
    locationValuesArr = R.insert(indexOfState, stateValue, locationValuesArr);
  }

  return createStringFromArray(locationValuesArr, ', ');
};

const concatContactFields = (entity, fields) => {
  if (R.isNil(entity)) return '';

  const defaultFields = ['contactName', 'email', 'phone'];

  const fieldsToPick = ifElse(
    isNilOrEmpty(fields),
    defaultFields,
    fields,
  );

  const contactValuesArr = R.values(
    R.pick(fieldsToPick, entity),
    entity,
  );

  return createStringFromArray(contactValuesArr, ', ');
};

const mergeFirstContactWithLocation = (location, useMultiEmails = false) => {
  let contact = R.compose(
    R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR),
    R.pathOr({}, ['contacts', 0]),
  )(location);

  if (isTrue(useMultiEmails)) {
    const emails = ifElse(
      isNotNilAndNotEmpty(getPropFromObject(GC.FIELD_EMAIL, contact)),
      R.of(Array, getPropFromObject(GC.FIELD_EMAIL, contact)),
      [],
    );

    contact = R.assoc(GC.FIELD_EMAILS, emails, contact);
  }

  return R.mergeRight(location, contact);
};

// Not used
// const setLocationContactFieldsToContactsArr = (location) => {
//   const firstContactPath = [GC.FIELD_CONTACTS, 0];
//   const contactFields = R.pick(GC.GROUPED_FIELDS.EVENT_CONTACT_ARR, location);
//   const contactFromArr = R.pathOr({}, firstContactPath, location);
//   const contact = mapObjectEmptyStringFieldsToNull(R.mergeRight(contactFromArr, contactFields));
//   const contacts = R.of(contact);

//   return R.assoc(
//     GC.FIELD_CONTACTS,
//     contacts,
//     R.omit(GC.GROUPED_FIELDS.EVENT_CONTACT_ARR, location),
//   );
// };

const getTemplateIdFromLocation = (location) => {
  const tId = R.path([GC.FIELD_TEMPLATE_ID, GC.FIELD_TEMPLATE_ID], location);
  const tId1 = R.path([GC.FIELD_TEMPLATE_ID, 'value'], location);
  const tId2 = R.path([GC.FIELD_TEMPLATE_ID], location);

  return R.or(tId, R.or(tId1, tId2));
};

const resetTemplateIdToLocation = (location) => R.assoc(
  GC.FIELD_TEMPLATE_ID, getTemplateIdFromLocation(location), location,
);

const getLocationTypeFromLocation = (location) => {
  const type1 = R.path([GC.FIELD_LOCATION_TYPE, 'value'], location);
  const type2 = R.path([GC.FIELD_LOCATION_TYPE], location);

  return R.or(type1, type2);
};

const resetLocationTypeToLocation = (location) => R.assoc(
  GC.FIELD_LOCATION_TYPE, getLocationTypeFromLocation(location), location,
);

const resetLocationTypeFromDropdownOption = (location) => {
  const type1 = R.path([GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], location);
  const type2 = R.path([GC.FIELD_LOCATION_TYPE], location);

  return R.assoc(
    GC.FIELD_LOCATION_TYPE, R.or(type1, type2), location,
  );
};

const mapSearchedLocation = R.curry((location) => {
  if (isNilOrEmpty(location)) return location;

  const locationWithoutSystem = R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR, location);
  const locationWithContacts = mergeFirstContactWithLocation(locationWithoutSystem);
  const locationType = R.path([GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], location);

  return R.assoc(GC.FIELD_LOCATION_TYPE, locationType, R.omit(GC.FIELD_CONTACTS, locationWithContacts));
});

const mapSearchedLocation2 = (location, options) => {
  if (isNilOrEmpty(location)) return location;

  const locationWithoutSystem = R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR, location);

  const locationWithContacts = mergeFirstContactWithLocation(
    locationWithoutSystem,
    getPropFromObject('useMultiEmails', options),
  );

  const locationType = R.path([GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], location);

  return R.assoc(GC.FIELD_LOCATION_TYPE, locationType, R.omit(GC.FIELD_CONTACTS, locationWithContacts));
};

const mapLocationFieldArray = (location, fieldName) => R.compose(
  R.map(R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR)),
  R.pathOr([], [fieldName]),
)(location);

const mapSearchedLocationWithContactsArr = R.curry((location) => {
  if (isNilOrEmpty(location)) return location;

  const locationWithoutSystem = R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_FULL_ARR, location);

  const { guid, templateId, templateGuid } = location;

  const contacts = mapLocationFieldArray(location, GC.FIELD_CONTACTS);
  const shippingHour = mapLocationFieldArray(location, GC.FIELD_SHIPPING_HOUR);
  const receivingHour = mapLocationFieldArray(location, GC.FIELD_RECEIVING_HOUR);
  const operationHour = mapLocationFieldArray(location, GC.FIELD_OPERATION_HOUR);
  const appointmentHour = mapLocationFieldArray(location, GC.FIELD_APPOINTMENT_HOUR);
  const locationType = R.path([GC.FIELD_LOCATION_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], location);
  const appointmentContact = R.prop(GC.FIELD_APPOINTMENT_CONTACT, location);

  return R.mergeRight(locationWithoutSystem, {
    contacts,
    locationType,
    shippingHour,
    receivingHour,
    operationHour,
    appointmentHour,
    [GC.FIELD_TEMPLATE_ID]: templateId,
    [GC.FIELD_TEMPLATE_GUID]: R.or(templateGuid, guid),
    [GC.FIELD_APPOINTMENT_CONTACT]: R.isNil(appointmentContact) ? null : omitObjectSystemFields(appointmentContact),
  });
});

const mapLocationHours = R.curry((location) => {
  const {
    fcfs,
    eventType,
    shippingHour,
    receivingHour,
    isAppointment,
    eventEarlyDate,
    appointmentDate,
    appointmentHour,
  } = location;

  const dateToUse = isAppointment ? appointmentDate : eventEarlyDate;
  const cond1 = R.and(R.not(fcfs), R.not(isAppointment));
  const cond2 = R.and(fcfs, isNilOrEmpty(eventType));
  const cond3 = R.and(fcfs, isNilOrEmpty(eventType));
  const cond4 = isAllTrue(
    isNilOrEmpty(shippingHour),
    isNilOrEmpty(receivingHour),
    isNilOrEmpty(appointmentHour),
  );

  if (isAnyTrue(cond1, cond2, cond3, cond4, isNilOrEmpty(dateToUse))) return location;

  const dayOfWeek = getDayOfWeek(dateToUse);
  const dayName = GC.daysOfWeek[dayOfWeek];

  if (R.isNil(dayName)) return location;

  const isPickup = isEventTypePickup(eventType);

  let propToUse = null;

  if (R.not(fcfs)) {
    propToUse = GC.FIELD_APPOINTMENT_HOUR;
  } else if (isPickup) {
    propToUse = GC.FIELD_SHIPPING_HOUR;
  } else {
    propToUse = GC.FIELD_RECEIVING_HOUR;
  }

  const hoursData = R.prop(propToUse, location);

  if (isNilOrEmpty(hoursData)) return location;

  const matchDay = R.find(
    ({ days }) => R.includes(dayName, days),
    hoursData,
  );

  if (R.isNil(matchDay)) return location;

  const { timeTo, timeFrom } = matchDay;

  const timeToFormatted = convertTimeToConfigFormat(timeTo);
  const timeFromFormatted = convertTimeToConfigFormat(timeFrom);
  const date = convertInstanceToDefaultDateFormat(dateToUse);

  return {
    ...location,
    [GC.FIELD_LOAD_EVENT_LATE_TIME]: timeToFormatted,
    [GC.FIELD_LOAD_EVENT_EARLY_TIME]: timeFromFormatted,
    [GC.FIELD_LOAD_EVENT_LATE_DATE]: `${date} ${timeToFormatted}`,
    [GC.FIELD_LOAD_EVENT_EARLY_DATE]: `${date} ${timeFromFormatted}`,
    [GC.FIELD_LOAD_APPOINTMENT_DATE]: fcfs ? R.or(appointmentDate, '') : date,
    [GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]: fcfs ?
      R.propOr('', GC.FIELD_LOAD_APPOINTMENT_LATE_TIME, location) : timeToFormatted,
    [GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME]: fcfs ?
      R.propOr('', GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME, location) : timeFromFormatted,
  };
});

const getLocationOptions = (locations) => R.map((location) => ({
  value: location[GC.FIELD_TEMPLATE_ID],
  label: `${location[GC.FIELD_TEMPLATE_ID]}, ${location[GC.FIELD_LOCATION_NAME]}, ${
    concatLocationFirstAndSecondAddresses(location)} ${concatLocationFields(location)}`,
}), locations);

const getTransformLocationsConfigs = () => ([
  { transform: '', main: true },
  {
    transform: 'rotate(72deg) translate(82%, -60%)',
    infoTransform: 'translate(-33%, calc(30px - 100%))',
  },
  {
    transform: 'rotate(144deg) translate(50%, -155%)',
    infoTransform: 'translate(-45%, calc(60px - 100%))',
  },
  {
    transform: 'rotate(216deg) translate(-50%, -155%)',
    infoTransform: 'translate(-55%, calc(60px - 100%))',
  },
  {
    transform: 'rotate(288deg) translate(-82%, -60%)',
    infoTransform: 'translate(-66%, calc(30px - 100%))',
  },
  {
    transform: 'rotate(324deg) translate(-50%, -100%)',
    infoTransform: 'translate(-70%, calc(-15px - 100%))',
  },
  {
    transform: 'rotate(36deg) translate(50%, -100%)',
    infoTransform: 'translate(-30%, calc(-15px - 100%))',
  },
  {
    transform: 'rotate(108deg) translate(82%,-200%)',
    infoTransform: 'translate(-25%, calc(60px - 100%))',
  },
  {
    transform: 'rotate(180deg) translate(0%,-260%)',
    infoTransform: 'translate(-50%, calc(90px - 100%))',
  },
  {
    transform: 'rotate(252deg) translate(-82%,-200%)',
    infoTransform: 'translate(-75%, calc(60px - 100%))',
  },
]);

const makeGroupedLocationsByLatLng = (locations) => {
  if (isNilOrEmpty(locations)) return [];

  let grouped = [];
  let lastLocations = [...locations];

  const makeOnSamePositionLocations = () => {
    const firstLocationsLatLng = R.path(['latLng'], R.head(lastLocations));

    const onSamePositionLocations = R.filter(
      (location) => R.equals(location.latLng, firstLocationsLatLng),
      lastLocations,
    );

    grouped = R.append(onSamePositionLocations, grouped);
    lastLocations = R.without(onSamePositionLocations, lastLocations);

    if (isNotNilAndNotEmpty(lastLocations)) {
      makeOnSamePositionLocations();
    }
  };

  makeOnSamePositionLocations();

  return grouped;
};

const makeLocationsWithTransform = (locations) => {
  const transformConfigs = getTransformLocationsConfigs();
  const groupedLocations = makeGroupedLocationsByLatLng(locations);

  const locationsWithTransform = R.reduce(
    (allLocations, locationGroup) => {
      const mapped = locationGroup.map(
        (location, index) => ({
          ...location,
          main: isTrue(R.path([index, 'main'], transformConfigs)),
          transform: R.path([index, 'transform'], transformConfigs),
          infoTransform: R.path([index, 'infoTransform'], transformConfigs),
        }),
      );

      return R.concat(allLocations, mapped);
    },
    [],
    groupedLocations,
  );

  return R.sort((item) => item.main, locationsWithTransform);
};

const getCountryAndStateByStringValues = ({ state, country }) => {
  const countryValue = R.prop(country, {
    'Canada': GC.ENUM_CA,
    'Mexico': GC.ENUM_MX,
    'United States': GC.ENUM_US,
  });

  const stateValue = R.compose(
    R.pathOr('', [state, GC.FIELD_VALUE]),
    R.indexBy(R.prop('labelFull')),
    R.prop(countryValue),
  )(GC.STATE_OPTIONS_MAP);

  return [countryValue, stateValue];
};

const getShortStateFromLocation = ({ state, country }) => {
  const countryValue = R.prop(country, {
    'Canada': GC.ENUM_CA,
    'Mexico': GC.ENUM_MX,
    'United States': GC.ENUM_US,
  });

  const stateValue = R.compose(
    R.pathOr('', [state, 'label']),
    R.indexBy(R.prop('labelFull')),
    R.prop(countryValue),
  )(GC.STATE_OPTIONS_MAP);

  if (isNilOrEmpty(stateValue)) return state;

  return stateValue;
};

export {
  getShortName,
  mapLocationHours,
  getLocationOptions,
  mapSearchedLocation,
  concatContactFields,
  mapSearchedLocation2,
  concatLocationFields,
  getStateValueByConfig,
  setStateValueByConfig,
  renderFullAddressString,
  renderSmallAddressString,
  renderAddressNameAndCode,
  getTemplateIdFromLocation,
  resetTemplateIdToLocation,
  getShortStateFromLocation,
  makeLocationsWithTransform,
  resetLocationTypeToLocation,
  getLocationTypeFromLocation,
  makeGroupedLocationsByLatLng,
  getTransformLocationsConfigs,
  mergeFirstContactWithLocation,
  renderAddressInfoWithNameAndCode,
  getCountryAndStateByStringValues,
  mapSearchedLocationWithContactsArr,
  resetLocationTypeFromDropdownOption,
  concatLocationFirstAndSecondAddresses,
  // setLocationContactFieldsToContactsArr,
};
